/**
 * Pagina di login (E10.1, TD-004; login Microsoft Entra ID con TC_AUTH_PROVIDER=entra).
 *
 * Modalità, mostrate se disponibili:
 *  - "Accedi con Microsoft": Entra ID via oidc-client-ts, configurato da /admin e letto a runtime
 *    da `GET /auth/sso-config` (vedi src/auth/entra.ts);
 *  - "Accedi con SSO": IdP OIDC di sviluppo (oidc-mock, VITE_OIDC_ISSUER — vedi src/auth/oidc.ts);
 *  - "Accesso amministratore locale": break-glass del product-service, per il primo avvio o se
 *    Entra è mal configurato.
 *
 * Dopo il login reindirizza alla pagina originalmente richiesta (state.from) oppure a /.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useAuth,
  OIDC_ISSUER,
  isOidcEnabled,
  startLogin,
  fetchSsoConfig,
  startEntraLogin,
  breakGlassLogin,
  type SsoConfig,
} from '../auth/index.js';
import styles from './LoginPage.module.css';

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const destination = (location.state as LocationState | null)?.from?.pathname ?? '/';
  const [sso, setSso] = useState<SsoConfig | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void fetchSsoConfig().then((cfg) => {
      if (alive) setSso(cfg);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) navigate(destination, { replace: true });
  }, [isAuthenticated, destination, navigate]);

  if (isAuthenticated) return null;

  function handleSsoLogin() {
    if (!OIDC_ISSUER) return;
    void startLogin(OIDC_ISSUER);
  }

  function handleEntraLogin() {
    if (!sso?.entra) return;
    setError(null);
    setPending(true);
    startEntraLogin(sso.entra, destination).catch((err: unknown) => {
      setPending(false);
      setError(
        err instanceof Error
          ? `Impossibile contattare Microsoft Entra ID: ${err.message}`
          : 'Impossibile contattare Microsoft Entra ID.',
      );
    });
  }

  async function handleBreakGlass(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      login(await breakGlassLogin(username.trim(), password));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Accesso non riuscito.');
    } finally {
      setPending(false);
      setPassword('');
    }
  }

  // Con l'IdP di sviluppo (default) il bottone SSO compare subito: niente "Caricamento…" in attesa
  // di /auth/sso-config, che senza broker risponde 404.
  const loading = sso === null && !isOidcEnabled();
  const nothingConfigured = !loading && !sso?.entra && !sso?.breakGlass && !isOidcEnabled();

  return (
    <div className={styles.page} data-testid="login-page">
      <div className={styles.card}>
        <h1 className={styles.brand}>
          Tenacious<span className={styles.brandAccent}>C</span>
        </h1>
        <p className={styles.tagline}>Portale CRA — Accesso</p>

        {loading && (
          <p className={styles.hint} data-testid="login-loading">
            Caricamento…
          </p>
        )}

        {sso?.entra && (
          <button
            type="button"
            className={styles.submitBtn}
            data-testid="btn-entra-login"
            onClick={handleEntraLogin}
            disabled={pending}
          >
            Accedi con Microsoft
          </button>
        )}

        {isOidcEnabled() && (
          <button
            type="button"
            className={styles.submitBtn}
            data-testid="btn-sso-login"
            onClick={handleSsoLogin}
          >
            Accedi con SSO
          </button>
        )}

        {sso?.breakGlass && (
          <details className={styles.breakGlass} open={!sso.entra && !isOidcEnabled()}>
            <summary data-testid="break-glass-toggle">Accesso amministratore locale</summary>
            <form
              className={styles.form}
              onSubmit={handleBreakGlass}
              data-testid="break-glass-form"
            >
              <div className={styles.field}>
                <label className={styles.label} htmlFor="bg-username">
                  Utente
                </label>
                <input
                  id="bg-username"
                  className={styles.input}
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  data-testid="break-glass-username"
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="bg-password">
                  Password
                </label>
                <input
                  id="bg-password"
                  type="password"
                  className={styles.input}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="break-glass-password"
                />
              </div>
              <button
                type="submit"
                className={styles.secondaryBtn}
                disabled={pending}
                data-testid="break-glass-submit"
              >
                Accedi
              </button>
            </form>
          </details>
        )}

        {error && (
          <p className={styles.error} role="alert" data-testid="login-error">
            {error}
          </p>
        )}

        {nothingConfigured && (
          <p className={styles.error} data-testid="sso-not-configured">
            Accesso SSO non configurato. Contattare l'amministratore di sistema.
          </p>
        )}
      </div>
    </div>
  );
}
